'use client';

import { useEffect, useState } from 'react';

import { SessionNotFound } from '@northladder/design-system';
import { IApiError } from '@northladder/utilities';

import { DeviceImagesListLoading } from './DeviceImagesListLoading';
import { DoneUploading } from './DoneUploading';
import { ErrorInFetchingList } from './ErrorInFetchingList';

interface INoImagesLoadedYetProps {
  error: IApiError | null;
  hasNoImages: boolean;
  hasNoSessionId: boolean;
  isDone: boolean;
  isLoading: boolean;
  onRestart: () => void;
  onRetry: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * This is rendered for all cases when no images list has been loaded so far
 * from the server, session not provided or expired.
 */
export function NoImagesLoadedYet({
  hasNoSessionId,
  isDone,
  isLoading,
  error,
  hasNoImages,
  onRestart,
  onRetry,
}: INoImagesLoadedYetProps) {
  /**
   * Adding the fallback state to show the loading state for the fallback.
   * Will show a simple spinner in the center of the screen for 2 seconds before
   * rendering the actual fallback state. That way simple API errors don't get
   * logged accidentally.
   */
  const [isPreparingFallback, setIsPreparingFallback] = useState(true);

  useEffect(() => {
    // Simulate loading by setting a timeout
    const timeoutId = setTimeout(() => {
      setIsPreparingFallback(false);
    }, 4000);

    // Clear the timeout to prevent memory leaks
    return () => {
      clearTimeout(timeoutId);
      setIsPreparingFallback(false);
    };
  }, []);

  if (isPreparingFallback) {
    return <DeviceImagesListLoading />;
  }

  if (hasNoSessionId) {
    return <SessionNotFound />;
  }

  if (isDone) {
    return <DoneUploading />;
  }

  if (isLoading) {
    return <DeviceImagesListLoading />;
  }

  if (error || hasNoImages) {
    return <ErrorInFetchingList onRestart={onRestart} onRetry={onRetry} />;
  }

  return null;
}
